import { useSelection } from '../../lib/selection';
import { isoWeekKey, isoWeekMonday, isoWeekSunday, monthKey, yearKey } from '../../lib/scopeKey';
import { NOTE_SCOPES, type Period } from '../../lib/app';
import { todayISO } from '../../lib/hooks';
import { useRef } from 'react';

/**
 * 统一日期筛选器 —— **笔记与新闻共用同一个日期源**。
 *
 * 改前两者的日期是各自独立的：新闻面板自带 `todayISO()` 的独立日期控件，
 * 笔记则只能被动跟随图表、没有自己的入口。于是想看「某天的笔记」必须绕道
 * 复盘页点月历，而想看「某天的新闻」得在面板里单独翻 —— 同一个"日期"概念
 * 有两处入口、两套状态，很容易各看各的。
 *
 * 现都读 `selection.date`（effectiveDate 的来源），故天然一致。
 *
 * scope 语义（周期同时决定图表周期与笔记 scope，保持"看着周K 写周记"不变）：
 *   日 → 交易日，`◀▶` 走交易日历（跳过周末与节假日）
 *   周/月/年 → 显示区间，`◀▶` 按自然周期推（它们没有"交易日"语义）
 */
export function DateScopeFilter() {
  const { period, setPeriod, effectiveDate, stepDate, setDate, date, latestTradingDay } = useSelection();

  /**
   * 取日入口：点中间那段时间唤起原生日期选择器。
   *
   * ⚠️ 不能直接把标签做成 <input type="date"> —— 周/月/年显示的是**区间**
   * （10-05 ~ 10-11），原生 input 表达不了。所以只能「自定义显示 +
   * 隐藏 input + showPicker()」。
   *
   * ⚠️ 隐藏 input 必须处于**已渲染**态（用 opacity:0 而非 display:none）：
   *    后者在部分浏览器调 showPicker() 会抛 InvalidStateError。
   *    tabIndex=-1 使它不进 Tab 序，但 focus() 兜底仍可用。
   */
  const pickerRef = useRef<HTMLInputElement>(null);
  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();   // 老浏览器没有 showPicker：focus 后可用回车/方向键改值
    }
  };

  /**
   * 是否已在最新交易日。
   *
   * date 为 null = 未显式选择，effectiveDate 回落到 latestTradingDay —— 与
   * 「最新」按钮 setDate(null) 是同一个操作。所以「最新」在已经最新时是
   * 空操作，置灰既避免误点，也兼作「当前不在最新」的指示：
   * 点过 K 线柱后日期被钉住，此时「最新」会亮成强调色。
   */
  const atLatest = date === null || date === latestTradingDay;

  if (!effectiveDate) {
    return (
      <div className="flex h-9 shrink-0 items-center gap-2 border-b px-3 text-[12px]"
        style={{ color: 'var(--text-3)' }}>
        正在获取交易日历…
      </div>
    );
  }

  const base = period === 'MIN1' || period === 'MIN5' ? 'D' : period;

  return (
    <div className="relative flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b px-2 py-1.5 text-[12px]">
      {/* scope 切换 = 切换图表周期，两者必须一致 */}
      <div className="flex items-center gap-0.5" role="tablist" aria-label="笔记周期">
        {NOTE_SCOPES.map((s) => {
          const p = s.id === 'day' ? 'D' : s.id === 'week' ? 'W' : s.id === 'month' ? 'M' : 'Y';
          const on = base === p;
          return (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setPeriod(p as Period)}
              className="rounded px-1.5 py-0.5 text-[11px]"
              style={{
                background: on ? 'var(--bg-elevated)' : 'transparent',
                color: on ? 'var(--text-1)' : 'var(--text-3)',
              }}
            >{s.label}</button>
          );
        })}
      </div>

      <span className="mx-0.5 h-3.5 w-px" style={{ background: 'var(--border)' }} />

      {/* 日期步进 */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => stepDate(-1)}
          aria-label="上一个"
          title="上一个（按交易日历/自然周期）"
          className="num rounded px-1.5 py-0.5 text-[12px] hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-2)' }}
        >‹</button>

        <button
          type="button"
          onClick={openPicker}
          aria-label={`选择日期，当前 ${effectiveDate}`}
          title="选择日期"
          className="num min-w-[128px] rounded px-1.5 py-0.5 text-center text-[12px] hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-1)', borderBottom: '1px dashed var(--border-strong)' }}
        >{scopeHint(base, effectiveDate)}</button>

        {/*
          取日真正靠这个隐藏 input。
          ⚠️ 上限是「今天」而不是 latestTradingDay：原来卡死在最后交易日，
             周六/周日/节假日根本选不进去 —— 而休市日是有新闻的（国庆能出
             几百条）。日历只知道「哪天开过市」，不知道「哪天出过消息」。
             今天允许给休市日写笔记/预测，不再强绑「一根 K 线 = 一个日期」。
          todayISO() 取 Asia/Shanghai，与服务端一致（库里 news.date 是北京时间）。
        */}
        <input
          ref={pickerRef}
          type="date"
          value={effectiveDate}
          max={todayISO()}
          tabIndex={-1}
          onChange={(e) => { if (e.target.value) setDate(e.target.value); }}
          aria-label="选择日期"
          className="pointer-events-none absolute h-px w-px opacity-0"
        />

        <button
          type="button"
          onClick={() => stepDate(1)}
          aria-label="下一个"
          title="下一个（按交易日历/自然周期）"
          className="num rounded px-1.5 py-0.5 text-[12px] hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-2)' }}
        >›</button>
      </div>

      {/* 回到最新：清掉显式日期，effectiveDate 回落到最新交易日 */}
      <button
        type="button"
        onClick={() => setDate(null)}
        disabled={atLatest}
        title={atLatest ? '已在最新交易日' : '回到最新交易日'}
        className="num rounded px-1.5 py-0.5 text-[11px] disabled:opacity-40"
        style={{ color: atLatest ? 'var(--text-3)' : 'var(--accent)' }}
      >最新</button>
    </div>
  );
}

/** scope + 日期 → 人类可读标签 */
function scopeHint(period: 'D' | 'W' | 'M' | 'Y', date: string): string {
  switch (period) {
    case 'D': return date;
    case 'W': {
      const k = isoWeekKey(date);
      const a = isoWeekMonday(k), b = isoWeekSunday(k);
      return a && b ? `${a.slice(5)} ~ ${b.slice(5)}` : date;
    }
    case 'M': return monthKey(date);
    case 'Y': return `${yearKey(date)} 年`;
  }
}
